import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';
import { copyObject } from '../utils/common';
import IHelpItem from '../Models/HelpAndResources/HelpItem.model';
import { HelpItemTypeEnum } from '../Enum/HelpItemTypeEnum';

const helpAndResources = createSlice({
    name: 'helpAndResources',
    initialState: { allHelpItems: [] as IHelpItem[], helpItem: {} as IHelpItem, isFetchingHelpItem: false, open: false },
    reducers: {
        _setAllHelpItems(state, action) {
            state.allHelpItems = action.payload;
        },
        _setHelpItem(state, action) {
            state.helpItem = action.payload;
        },
        _setIsFetchingHelpItem(state, action) {
            state.isFetchingHelpItem = action.payload;
        },
        _setOpen(state, action) {
            state.open = action.payload;
        },
    },
});

const { _setAllHelpItems, _setHelpItem, _setIsFetchingHelpItem, _setOpen } = helpAndResources.actions;

export const setHelpItem =
    (helpItem: IHelpItem): AppThunk =>
    async (dispatch) => {
        dispatch(_setHelpItem(helpItem));
    };

export const setIsFetchingHelpItem =
    (isFetching: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingHelpItem(isFetching));
    };

export const setOpen =
    (open: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setOpen(open));
    };

export const getAllHelpItems = (): AppThunk => async (dispatch) => {
    try {
        dispatch(_setIsFetchingHelpItem(true));
        const url = `${API_URL}/helpandresources/getallhelpitems`;
        const response = await axios.get(url);
        dispatch(_setAllHelpItems(response.data));
    } catch (error) {
        console.error('Error fetching help items:', error);
    } finally {
        dispatch(_setIsFetchingHelpItem(false));
    }
};

export const saveHelpItem =
    (helpItem: IHelpItem): AppThunk =>
    async (dispatch, getState) => {
        try {
            dispatch(_setIsFetchingHelpItem(true));
            const endpoint = helpItem.Type === HelpItemTypeEnum.DOCUMENT ? 'inserthelpdocument' : 'inserthelpvideo';
            const url = `${API_URL}/helpandresources/${endpoint}`;
            const response = await axios.post(url, helpItem);
            const saved = response.data as IHelpItem;
            dispatch(_setHelpItem(saved));
            const helpItems = getState().helpAndResources.allHelpItems as IHelpItem[];
            const updatedHelpItems = helpItems.some((item) => item.Id === saved.Id)
                ? helpItems.map((item) => (item.Id === saved.Id ? saved : item))
                : [saved, ...helpItems];
            dispatch(_setAllHelpItems(updatedHelpItems));
        } catch (error) {
            console.error('Error saving help item:', error);
        } finally {
            dispatch(_setIsFetchingHelpItem(false));
            dispatch(setOpen(false));
        }
    };

export const updateHelpItem =
    (helpItem: IHelpItem): AppThunk =>
    async (dispatch, getState) => {
        try {
            const helpItems = getState().helpAndResources.allHelpItems;
            dispatch(_setIsFetchingHelpItem(true));
            const url = `${API_URL}/helpandresources/updatehelpitem?id=${helpItem.Id}`;
            await axios.put(url, helpItem);
            const updatedHelpItems = helpItems.map((item: IHelpItem) => (item.Id === helpItem.Id ? { ...item, ...helpItem } : item));
            dispatch(_setAllHelpItems(updatedHelpItems));
        } catch (error) {
            console.error('Error updating help item:', error);
        } finally {
            dispatch(_setIsFetchingHelpItem(false));
            dispatch(setOpen(false));
        }
    };

export const deleteHelpItem =
    (helpItemId: string): AppThunk =>
    async (dispatch, getState) => {
        try {
            dispatch(_setIsFetchingHelpItem(true));
            const url = `${API_URL}/helpandresources/deletehelpitem?id=${helpItemId}`;
            await axios.delete(url);
            dispatch(_setHelpItem({} as IHelpItem));
            const helpItems = getState().helpAndResources.allHelpItems;
            const updatedHelpItems = helpItems.filter((item: IHelpItem) => item.Id !== helpItemId);
            dispatch(_setAllHelpItems(updatedHelpItems));
        } catch (error) {
            console.error('Error deleting help item:', error);
        } finally {
            dispatch(_setIsFetchingHelpItem(false));
            dispatch(setOpen(false));
        }
    };

export default helpAndResources.reducer;
